<template>
  <base-view-layout class="project-show">
    <template v-if="projectInformation" #header>
      <div class="project-heading">
        <div class="project-info">
          <h1 class="uppercase" :class="{ 'mb-0': $mq !== 'sm' }">
            {{ projectInformation.name }}
          </h1>
          <p class="type" v-if="$mq === 'sm'">
            <i
              class="fa-fw text-blue"
              :class="iconMap[projectInformation.type]"
            />
            {{ projectInformation.type }}
          </p>

          <p class="mt-6">
            {{ projectInformation.place.address_1 }}
          </p>
          <p v-if="projectInformation.place.address_2">
            {{ projectInformation.place.address_2 }}
          </p>
          <p>
            {{ projectInformation.place.cityStateZip }}

            <a
              class="ml-1 text-red hover-bold"
              :href="generateGoogleMapsUrl(projectInformation.place)"
            >
              <i class="fal fa-map-marker-alt"></i>
            </a>
          </p>
          <div v-if="!isProjectPrimaryContact">
            <p class="mt-2">
              <a
                class="text-red hover-bold"
                :href="`mailto:${projectInformation.email}`"
              >
                {{ projectInformation.email }}
              </a>
            </p>
            <phone-number-with-type
              :phone-number="projectInformation.primary_phone"
            />
          </div>
          <phone-number-with-type
            :phone-number="number"
            :key="`project_number_${i}`"
            v-for="(number, i) in projectInformation.additional_phones"
          />
        </div>
        <div class="buttons-container" v-if="$mq !== 'sm'">
          <p class="type">
            <i
              class="fa-fw text-blue"
              :class="iconMap[projectInformation.type]"
            />
            {{ projectInformation.type }}
          </p>
        </div>
        <div class="primary-contact-container flex md:text-lg">
          <div class="mr-2 font-bold">Primary Contact:</div>
          <div class="mr-2">
            <p>{{ projectInformation.primary_contact.name }}</p>
            <p v-if="projectInformation.primary_contact.email">
              <a
                class="text-red"
                :href="`mailto:${projectInformation.primary_contact.email}`"
                >{{ projectInformation.primary_contact.email }}</a
              >
            </p>
            <p v-if="projectInformation.primary_contact.primary_phone.number">
              {{ projectInformation.primary_contact.primary_phone.number }}
            </p>
          </div>
        </div>

        <div class="additional-contacts-container flex">
          <template
            v-if="
              projectInformation.additional_contacts &&
                projectInformation.additional_contacts.length
            "
          >
            <div class="mr-2">Additional Contacts:</div>
            <div
              v-for="contact in projectInformation.additional_contacts"
              :key="`contact_${contact.id}`"
              class=" mr-2"
            >
              <p>{{ contact.name }}</p>
              <p>
                {{ contact.primary_place.address_1 }}<br />
                {{ contact.primary_place.city }},
                {{ contact.primary_place.state }}
                {{ contact.primary_place.zip }}
              </p>
              <p v-if="contact.email" class="mt-2">
                <a class="text-red" :href="`mailto:${contact.email}`">{{
                  contact.email
                }}</a>
              </p>
              <p v-if="contact.primary_phone">
                {{ contact.primary_phone.number }}
              </p>
            </div>
          </template>
        </div>
      </div>
    </template>

    <template #body>
      <div class="w-full">
        <div class="w-full">
          <stacked-tabs
            :tabs="tabs"
            :selected-tab="selectedTab"
            @tab-selected="selectedTab = $event"
          />
          <template v-if="$mq === 'sm'">
            <contract-cell
              class="block md:hidden"
              v-for="(contract, index) in selectedTabContracts"
              :key="`${contract.name}-${index}`"
              :contract="contract"
            />
          </template>
          <bdg-table-view
            :rows="selectedTabContracts"
            :columns="columns"
            empty-state-text="No contracts to view."
            v-else
          >
            <template #beside-pagination v-if="userType === 'admin'">
              <bdg-button
                class="uppercase"
                before-icon-class="bdg-icon plus fa-fw"
                @click.native="addContract"
              >
                New Contract
              </bdg-button>
            </template>
          </bdg-table-view>
        </div>
      </div>
    </template>
  </base-view-layout>
</template>

<script>
import { Project } from "../../models/Project";
import BaseViewLayout from "../layouts/BaseViewLayout";
import ContractForm from "../contracts/ContractForm";
import StackedTabs from "../../elements/StackedTabs";
import BdgTableView from "../../elements/table-view/BdgTableView";
import BdgButton from "../../elements/BdgButton";
import ContractCell from "../../elements/list-view/list-view-cells/ContractCell";
import ProjectForm from "../../forms/ProjectForm";
import { ProjectIcons } from "../../js-data/icon-maps";
import AddressCell from "../../elements/table-view/special-cells/AddressCell";
import GenericDeleteModal from "../../components/modals/GenericDeleteModal";
//import ProjectContactInfo from "../../elements/ProjectContactInfo";
import PhoneNumberWithType from "../../elements/PhoneNumberWithType";
//import BdgAside from "../../elements/BdgAside";
import EmailCell from "../../elements/table-view/special-cells/EmailCell";

export default {
  name: "ProjectShow",
  components: {
    PhoneNumberWithType,
    ContractCell,
    BdgButton,
    BdgTableView,
    StackedTabs,
    BaseViewLayout
  },
  props: {
    id: [String, Number],
    project: {
      type: Project
    }
  },
  data() {
    return {
      iconMap: ProjectIcons,
      selectedTab: null
    };
  },
  computed: {
    userType() {
      return this.$store.state.userType;
    },
    projectInformation() {
      const project = this.$store.state.Projects.project;
      if (project && project.additional_contacts) {
        project.additional_contacts = project.additional_contacts.map(c => {
          return {
            ...c,
            label: c.name
          };
        });
      }
      return project;
    },
    isProjectPrimaryContact() {
      const project = this.$store.state.Projects.project;
      try {
        if (
          project.email.toLowerCase() ===
            project.primary_contact.email.toLowerCase() &&
          project.primary_phone.number ===
            project.primary_contact.primary_phone.number
        ) {
          return true;
        } else {
          return false;
        }
      } catch {
        return false;
      }
    },
    tabs() {
      return this.$store.state.Projects.showTabs;
    },
    columnActions() {
      const actions = [
        {
          label: "view",
          clickAction: contract => {
            this.viewContract(contract);
          }
        },
        {
          label: "edit",
          clickAction: contract => {
            this.$store.commit("setCreateEditComponentParams", {
              createEditComponent: ContractForm,
              createEditComponentData: contract,
              createEditComponentIsShown: true
            });
          }
        },
        {
          label: "delete",
          clickAction: contract => {
            this.$store.commit("setConfirmationModalParams", {
              type: "delete",
              headerText: "Delete Confirmation",
              bodyComponent: GenericDeleteModal,
              bodyComponentData: {
                entityName: "contract",
                deleteFunction: () => {
                  this.deleteContract(contract);
                }
              },
              isShown: true
            });
          }
        }
      ];

      return this.userType === "admin" ? actions : actions.slice(0, 2);
    },
    columns() {
      return [
        { label: "contract name", property: "name" },
        { label: "contact", property: "primaryContactName" },
        {
          label: "address",
          property: "nestedPlace",
          cellComponent: AddressCell
        },
        { label: "email", property: "nestedEmail", cellComponent: EmailCell },
        { label: "phone", property: "primaryPhoneNumber" },
        {
          label: "actions",
          property: "actions",
          actions: this.columnActions,
          additionalCellClasses: "w-px whitespace-no-wrap"
        }
      ];
    },
    projectContracts() {
      if (this.projectInformation && this.projectInformation.contracts) {
        return this.projectInformation.contracts.map(contract => {
          contract.nestedPlace = { place: contract.place };
          contract.nestedEmail = { email: contract.email };
          return contract;
        });
      }
      return [];
    },
    selectedTabContracts() {
      if (this.selectedTab && this.projectContracts) {
        return this.projectContracts.filter(contract => {
          return contract.type === this.selectedTab.slug;
        });
      }
      return [];
    }
  },
  created() {
    this.$store.dispatch("getProject", this.id);
  },
  methods: {
    viewContract(contract) {
      this.$router.push({
        name:
          this.userType === "admin" ? "contracts-show" : "contract-contacts",
        params: { id: contract.id, contract: contract }
      });
    },
    editProject() {
      this.$store.commit("setCreateEditComponentParams", {
        createEditComponent: ProjectForm,
        createEditComponentData: this.projectInformation,
        createEditComponentIsShown: true
      });
    },
    deleteProject() {
      this.$store.commit("setConfirmationModalParams", {
        type: "delete",
        headerText: "Delete Confirmation",
        bodyComponent: GenericDeleteModal,
        bodyComponentData: {
          entityName: "project",
          deleteFunction: () =>
            this.$store
              .dispatch("deleteProject", this.projectInformation)
              .then(res => {
                this.$store.dispatch("handleApiResponse", {
                  responseStatus: res,
                  successText: "Project deleted successfully.",
                  errorText:
                    "An error occurred while attempting to delete Project.",
                  afterSuccessFunction: () => {
                    if (this.$store.state.Search.areSearchResultsShown) {
                      this.$store.dispatch(
                        "searchProjects",
                        this.$store.state.Search.searchTerm
                      );
                    } else {
                      this.$store.dispatch("getProjects");
                    }
                    this.$store.commit("resetConfirmationModalParams");
                    this.$router.push({ name: "projects" });
                  }
                });
              })
        },
        isShown: true
      });
    },
    deleteContract(contract) {
      this.$store.dispatch("deleteContract", contract).then(res => {
        this.$store.dispatch("handleApiResponse", {
          responseStatus: res,
          successText: "Contract deleted successfully.",
          errorText: "An error occurred while attempting to delete Contract.",
          afterSuccessFunction: () => {
            this.$store.dispatch("getProject", this.id);
            this.$store.commit("resetConfirmationModalParams");
          }
        });
      });
    },
    addContract() {
      this.$store.commit("setCreateEditComponentParams", {
        createEditComponent: ContractForm,
        createEditComponentData: {
          project_id: this.projectInformation.id,
          primary_contact_id: this.projectInformation.primary_contact_id,
          place: this.projectInformation.place,
          email: this.projectInformation.email,
          primary_phone: this.projectInformation.primary_phone
        },
        createEditComponentIsShown: true
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.project-show {
  .project-heading {
    display: grid;
    grid-row-gap: 1.25rem;
    grid-template-columns: auto 1fr auto;
    grid-template-rows: auto;
    grid-template-areas:
      "project-info . buttons"
      "primary-contact additional-contacts additional-contacts";

    @screen md {
      grid-column-gap: 1.75rem;
    }

    @screen xl {
      grid-column-gap: 3.5rem;

      @apply mb-6;
    }

    .project-info {
      grid-area: project-info;

      p {
        @apply leading-tight;

        @screen xl {
          @apply text-lg;
        }
      }
    }

    .buttons-container {
      grid-area: buttons;
    }

    .type {
      @apply text-sm uppercase;

      @screen xl {
        @apply text-base;
      }
    }

    .primary-contact-container {
      grid-area: primary-contact;

      .project-contact-info {
        @screen xl {
          @apply text-lg;
        }
      }
    }

    .additional-contacts-container {
      grid-area: additional-contacts;

      .additional-contacts {
        @apply flex flex-wrap;

        .project-contact-info {
          @screen lg {
            width: calc(33.333333% - 0.5rem);
          }

          @screen xl {
            width: calc(25% - 2.25rem);
            @apply text-lg;
          }

          &:not(:nth-child(3n)) {
            @screen lg {
              @apply mr-3;
            }

            &:nth-child(4n) {
              @screen xl {
                @apply mr-0;
              }
            }
          }

          &:nth-child(3n):not(:nth-child(4n)),
          &:not(:nth-child(4n)) {
            @screen xl {
              @apply mr-12;
            }
          }

          &:nth-child(n + 4) {
            @screen lg {
              @apply mt-2;
            }
          }
        }
      }
    }
  }
}
</style>
